import './PercentChange.scss';
import cn from 'classnames';
import { MdCallMade, MdChangeHistory } from 'react-icons/md';
import { Size, SizeType, StretchMode, StretchModeType } from '../designConstants';

interface PercentChangeProps {
  // Scales large changes to K/M (e.g. "+1K%"); false shows the full value with commas.
  abbreviate?: boolean;
  decimals?: number;
  // Some metrics are "lower is better" (e.g. CPM, CPX) — a negative percentChange for those
  // is a good outcome and should render with the "positive" (green) tone, not "inverted".
  // The arrow still points in the change's true direction; only the pill's color/tone flips.
  invertTone?: boolean;
  negativeColor?: string;
  percentChange: number;
  size?: SizeType;
  stretchMode?: StretchModeType;
}

interface FormatPercentChangeLabelOptions {
  decimals?: number;
  // Collapse |pct| < 1 to "-<1%" / "<1%"; defaults to true when decimals === 0.
  collapseSubOne?: boolean;
  abbreviate?: boolean;
}

const SCALE_M = 1_000_000;
const SCALE_K = 1_000;

// Shared label formatter so charts, tooltips, and tables emit identical text.
// Handles K/M scaling, sub-1% collapse, ±Infinity → "+∞%" / "-∞%", and explicit
// "+" / "-" prefixes so positive deltas read as "+5%" not "5%" — matches the
// signed dollar formatting (e.g. "+$5K") used in the same surfaces.
export const formatPercentChangeLabel = (
  percentChange: number,
  {
    decimals = 0,
    collapseSubOne = decimals === 0,
    abbreviate = true
  }: FormatPercentChangeLabelOptions = {}
): string => {
  if (Number.isNaN(percentChange)) {
    return '—';
  }
  if (!Number.isFinite(percentChange)) {
    return percentChange > 0 ? '+∞%' : '-∞%';
  }
  const absPercent = Math.abs(percentChange);
  if (collapseSubOne && percentChange !== 0 && absPercent < 1) {
    return `${percentChange < 0 ? '-' : '+'}<1%`;
  }
  const sign = percentChange > 0 ? '+' : '';
  if (!abbreviate) {
    const value = percentChange.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${sign}${value}%`;
  }
  let scale = '';
  let scaleDivisor = 1;
  if (absPercent >= SCALE_M) {
    scale = 'M';
    scaleDivisor = SCALE_M;
  } else if (absPercent >= SCALE_K) {
    scale = 'K';
    scaleDivisor = SCALE_K;
  }
  return `${sign}${(percentChange / scaleDivisor).toFixed(decimals)}${scale}%`;
};

export const PercentChange = ({
  abbreviate = true,
  decimals = 0,
  invertTone = false,
  negativeColor,
  percentChange,
  size = Size.MD,
  stretchMode = StretchMode.FILL
}: PercentChangeProps) => {
  const toneChange = invertTone ? -percentChange : percentChange;
  const tone = (() => {
    if (percentChange === 0 || Number.isNaN(percentChange)) return 'zero';
    if (toneChange < 0) return 'inverted';
    return 'positive';
  })();
  const label = formatPercentChangeLabel(percentChange, { decimals, abbreviate });
  const style = tone === 'inverted' && negativeColor ? { background: negativeColor } : undefined;
  const icon =
    percentChange === 0 ? (
      <MdChangeHistory className='percent-arrow' />
    ) : (
      <MdCallMade className={cn('percent-arrow', { inverted: percentChange < 0 })} />
    );

  return (
    <div className={cn('percent-change', tone, size, stretchMode)} style={style}>
      <div className='percent-change-icon'>{icon}</div>
      <div className='percent-change-label'>{label}</div>
    </div>
  );
};
